import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Edit, Save, X } from 'lucide-react';

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedUserBanks, setExpandedUserBanks] = useState({});
  const [userBankAccounts, setUserBankAccounts] = useState({});
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankForm, setBankForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    phone: '',
    role: 'user'
  });
  const [createdUserPassword, setCreatedUserPassword] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    // Prevent Admin from editing SuperAdmin
    if (user.role === 'superadmin' && currentUser?.role !== 'superadmin') {
      toast.error('Admin kann SuperAdmin-Benutzer nicht bearbeiten');
      return;
    }
    
    setEditingUser(user.id);
    setEditForm({
      username: user.username,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      is_verified: user.is_verified
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleSaveEdit = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}`, editForm);
      toast.success('Benutzer erfolgreich aktualisiert');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (userId, username) => {
    // Find the user to check role
    const userToDelete = users.find(u => u.id === userId);
    
    // Prevent Admin from deleting SuperAdmin
    if (userToDelete?.role === 'superadmin' && currentUser?.role !== 'superadmin') {
      toast.error('Admin kann SuperAdmin-Benutzer nicht löschen');
      return;
    }
    
    if (!window.confirm(`Möchten Sie den Benutzer "${username}" wirklich löschen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Benutzer erfolgreich gelöscht');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Passwort zurücksetzen?\n\nEin sicheres Passwort wird automatisch generiert und per E-Mail an den Benutzer gesendet. Der Benutzer sollte es nach dem Login ändern.')) {
      return;
    }

    try {
      const response = await axios.put(`${API}/users/${userId}/password`);
      
      toast.success(
        `Passwort wurde zurückgesetzt!\n📧 E-Mail wurde an ${response.data.email} gesendet`,
        { duration: 5000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
    }
  };

  const handleManualVerify = async (userId, username) => {
    if (!window.confirm(`"${username}" manuell als verifiziert markieren, ohne dass die Bestätigungs-E-Mail geklickt wird?`)) {
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${userId}/verify`);
      toast.success('Benutzer wurde manuell verifiziert');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim manuellen Verifizieren');
    }
  };

  const handleUpdateCredentials = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${userId}/credentials`, {
        username: editForm.username,
        email: editForm.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Login-Daten erfolgreich aktualisiert');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Login-Daten');
    }
  };

  const handleCreateUser = async () => {
    // Validate form
    if (!newUserForm.username || !newUserForm.email || !newUserForm.password) {
      toast.error('Benutzername, E-Mail und Passwort sind erforderlich');
      return;
    }

    if (newUserForm.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/users`, newUserForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Benutzer erfolgreich erstellt');
      
      // Store password to show once
      setCreatedUserPassword({
        username: newUserForm.username,
        email: newUserForm.email,
        password: newUserForm.password
      });
      
      setShowCreateUserModal(false);
      setNewUserForm({
        username: '',
        email: '',
        password: '',
        phone: '',
        role: 'user'
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    }
  };

  const handleRequestVerification = async (userId, userEmail) => {
    if (!window.confirm(`Möchten Sie eine Verifizierungs-E-Mail an ${userEmail} senden?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/users/${userId}/request-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verifizierungs-E-Mail wurde gesendet');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Verifizierungs-E-Mail');
    }
  };

  const toggleUserBankDetails = async (userId) => {
    if (expandedUserBanks[userId]) {
      setExpandedUserBanks(prev => ({ ...prev, [userId]: false }));
    } else {
      // Load bank accounts for this user
      try {
        const response = await axios.get(`${API}/user-bank-accounts/${userId}`);
        setUserBankAccounts(prev => ({ ...prev, [userId]: response.data }));
        setExpandedUserBanks(prev => ({ ...prev, [userId]: true }));
      } catch (error) {
        toast.error('Fehler beim Laden der Bankdaten');
      }
    }
  };

  const startEditBankAccount = (userId, account) => {
    setEditingBankAccount(`${userId}-${account.project_id}`);
    setBankForm({
      account_holder: account.account_holder || '',
      iban: account.iban || '',
      bic: account.bic || '',
      bank_name: account.bank_name || ''
    });
  };

  const cancelEditBankAccount = () => {
    setEditingBankAccount(null);
    setBankForm({});
  };

  const saveBankAccount = async (userId, projectId) => {
    if (!bankForm.account_holder || !bankForm.iban) {
      toast.error('Kontoinhaber und IBAN sind erforderlich');
      return;
    }

    try {
      await axios.put(`${API}/user-bank-accounts/${userId}/${projectId}`, {
        project_id: projectId,
        account_holder: bankForm.account_holder,
        iban: bankForm.iban,
        bic: bankForm.bic || '',
        bank_name: bankForm.bank_name || ''
      });
      toast.success('Bankdaten erfolgreich gespeichert');
      setEditingBankAccount(null);
      setBankForm({});
      // Reload bank accounts
      const response = await axios.get(`${API}/user-bank-accounts/${userId}`);
      setUserBankAccounts(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg text-slate-600">Benutzer werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="admin-users-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/bess/dashboard')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Zurück zum Dashboard
          </Button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Benutzerverwaltung</h1>
              <p className="text-slate-600 mt-2">Verwalten Sie alle registrierten Benutzer</p>
            </div>
            {currentUser?.role === 'superadmin' && (
              <Button onClick={() => setShowCreateUserModal(true)} className="bg-green-600 hover:bg-green-700">
                ➕ Neuer Benutzer
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {users.map((user, index) => (
            <div key={user.id} className="card" data-testid={`user-card-${index}`}>
              {/* User Header */}
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {editingUser === user.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Benutzername</Label>
                          <Input
                            value={editForm.username}
                            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">E-Mail</Label>
                          <Input
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      {currentUser?.role === 'superadmin' && (
                        <div>
                          <Label className="text-xs">Rolle</Label>
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="investor">Investor</option>
                            <option value="admin">Admin</option>
                            <option value="finance">Finance</option>
                          </select>
                          <p className="text-xs text-slate-500 mt-1">
                            ⚠️ SuperAdmin-Rolle kann nicht vergeben werden
                          </p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveEdit(user.id)} className="bg-green-600 hover:bg-green-700">
                          <Save size={16} className="mr-1" />
                          Speichern
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-slate-900">{user.username}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin' ? 'bg-red-100 text-red-800' :
                          user.role === 'finance' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'content_filler' ? 'bg-purple-100 text-purple-800' :
                          user.role === 'superadmin' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {user.role}
                        </span>
                        {user.is_verified ? (
                          <span className="text-green-600 font-semibold text-sm">✓ Verifiziert</span>
                        ) : (
                          <span className="text-red-600 text-sm">✗ Nicht verifiziert</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      {user.phone && <p className="text-sm text-slate-600">📞 {user.phone}</p>}
                      <p className="text-xs text-slate-500 mt-2">Registriert: {new Date(user.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {user.role === 'superadmin' && currentUser?.role !== 'superadmin' ? (
                    <span className="text-sm text-slate-500 italic">Geschützt</span>
                  ) : editingUser !== user.id && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(user)} data-testid={`edit-user-${index}`}>
                        <Edit size={16} className="mr-1" />
                        Bearbeiten
                      </Button>
                      {currentUser?.role === 'superadmin' && (
                        <>
                          <Button size="sm" variant="outline" className="bg-blue-50 text-blue-600 hover:bg-blue-100" onClick={() => handleResetPassword(user.id)}>
                            Passwort
                          </Button>
                          <Button size="sm" variant="outline" className="bg-amber-50 text-amber-600 hover:bg-amber-100" onClick={() => handleRequestVerification(user.id, user.email)}>
                            ✉️ Verifizierung
                          </Button>
                        </>
                      )}
                      {!user.is_verified && (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                        <Button size="sm" variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100" onClick={() => handleManualVerify(user.id, user.username)}>
                          ✓ Manuell verifizieren
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(user.id, user.username)}
                        data-testid={`delete-user-${index}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                      {user.role === 'user' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-50 text-green-700 hover:bg-green-100"
                          onClick={() => toggleUserBankDetails(user.id)}
                        >
                          {expandedUserBanks[user.id] ? '▼ Bankdaten' : '▶ Bankdaten'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Bank Details Section */}
              {expandedUserBanks[user.id] && user.role === 'user' && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-lg font-bold text-slate-900 mb-4">Auszahlungskonten</h4>
                  {(!userBankAccounts[user.id] || userBankAccounts[user.id].length === 0) ? (
                    <p className="text-sm text-slate-600">Keine Auszahlungskonten hinterlegt</p>
                  ) : (
                    <div className="space-y-4">
                      {userBankAccounts[user.id].map((account) => {
                        const isEditing = editingBankAccount === `${user.id}-${account.project_id}`;
                        const project = projects.find(p => p.id === account.project_id);

                        return (
                          <div key={account.project_id} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-3 flex justify-between items-center">
                              <div>
                                <h5 className="font-bold text-slate-900">{account.project_title || project?.title || account.project_id}</h5>
                                {account.status === 'approved' && (
                                  <span className="text-xs text-green-600 font-semibold">✓ Genehmigt</span>
                                )}
                                {account.status === 'pending' && (
                                  <span className="text-xs text-yellow-600 font-semibold">⏳ Wartet auf Genehmigung</span>
                                )}
                                {account.status === 'rejected' && (
                                  <span className="text-xs text-red-600 font-semibold">✗ Abgelehnt</span>
                                )}
                              </div>
                              {!isEditing && (
                                <Button size="sm" variant="outline" onClick={() => startEditBankAccount(user.id, account)}>
                                  <Edit size={14} className="mr-1" />
                                  Bearbeiten
                                </Button>
                              )}
                            </div>
                            <div className="p-4 bg-white">
                              {isEditing ? (
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-xs">Kontoinhaber *</Label>
                                    <Input
                                      value={bankForm.account_holder}
                                      onChange={(e) => setBankForm({...bankForm, account_holder: e.target.value})}
                                      placeholder="Max Mustermann"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">IBAN *</Label>
                                    <Input
                                      value={bankForm.iban}
                                      onChange={(e) => setBankForm({...bankForm, iban: e.target.value})}
                                      placeholder="DE89370400440532013000"
                                      className="font-mono"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">BIC</Label>
                                      <Input
                                        value={bankForm.bic}
                                        onChange={(e) => setBankForm({...bankForm, bic: e.target.value})}
                                        placeholder="COBADEFFXXX"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Bank</Label>
                                      <Input
                                        value={bankForm.bank_name}
                                        onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                                        placeholder="Commerzbank"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => saveBankAccount(user.id, account.project_id)} className="bg-green-600 hover:bg-green-700">
                                      <Save size={14} className="mr-1" />
                                      Speichern
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditBankAccount}>
                                      Abbrechen
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Kontoinhaber:</span>
                                    <span className="font-medium">{account.account_holder}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">IBAN:</span>
                                    <span className="font-mono font-medium">{account.iban}</span>
                                  </div>
                                  {account.bic && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">BIC:</span>
                                      <span className="font-medium">{account.bic}</span>
                                    </div>
                                  )}
                                  {account.bank_name && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Bank:</span>
                                      <span className="font-medium">{account.bank_name}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {users.length === 0 && (
            <div className="text-center py-12 text-slate-600">
              Keine Benutzer gefunden.
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateUserModal(false)}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Neuer Benutzer erstellen</h2>
              
              <div className="space-y-4">
                <div>
                  <Label>Benutzername *</Label>
                  <Input
                    value={newUserForm.username}
                    onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                    placeholder="z.B. max.mustermann"
                  />
                </div>

                <div>
                  <Label>E-Mail *</Label>
                  <Input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    placeholder="max@beispiel.de"
                  />
                </div>

                <div>
                  <Label>Passwort * (mind. 6 Zeichen)</Label>
                  <Input
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                    placeholder="••••••"
                  />
                </div>

                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={newUserForm.phone}
                    onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
                    placeholder="+49 123 456789"
                  />
                </div>

                <div>
                  <Label>Rolle *</Label>
                  <select
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user">User (Investor)</option>
                    <option value="admin">Admin</option>
                    <option value="finance">Finance</option>
                    <option value="content_filler">Content Filler</option>
                    <option value="superadmin">SuperAdmin</option>
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    ℹ️ Der Benutzer wird automatisch verifiziert und erhält eine Willkommens-E-Mail mit den Login-Daten.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button onClick={handleCreateUser} className="flex-1 bg-green-600 hover:bg-green-700">
                  Erstellen
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUserForm({ username: '', email: '', password: '', phone: '', role: 'user' });
                }} className="flex-1">
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Created User Password Display - ONE TIME ONLY */}
        {createdUserPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setCreatedUserPassword(null)}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Benutzer erstellt!</h2>
                <p className="text-slate-600 mt-2">Bitte speichern Sie das Passwort jetzt - es wird nur EINMAL angezeigt.</p>
              </div>
              
              <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Benutzername</Label>
                  <p className="text-slate-900 font-mono">{createdUserPassword.username}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">E-Mail</Label>
                  <p className="text-slate-900 font-mono">{createdUserPassword.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Passwort</Label>
                  <div className="flex gap-2 items-center">
                    <p className="text-slate-900 font-mono font-bold text-lg flex-1">{createdUserPassword.password}</p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(createdUserPassword.password);
                        toast.success('Passwort kopiert!');
                      }}
                    >
                      📋 Kopieren
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-amber-800">
                  ⚠️ <strong>Wichtig:</strong> Das Passwort kann nach dem Schließen nicht mehr abgerufen werden!
                </p>
              </div>

              <Button onClick={() => setCreatedUserPassword(null)} className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                Verstanden, fortfahren
              </Button>
            </div>
          </div>
        )}

        {/* Reset Password Display - ONE TIME ONLY */}
        {resetPasswordData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setResetPasswordData(null)}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Passwort zurückgesetzt!</h2>
                <p className="text-slate-600 mt-2">Bitte speichern Sie das neue Passwort jetzt - es wird nur EINMAL angezeigt.</p>
              </div>
              
              <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Benutzer</Label>
                  <p className="text-slate-900 font-mono">{resetPasswordData.username} ({resetPasswordData.email})</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Neues Passwort</Label>
                  <div className="flex gap-2 items-center">
                    <p className="text-slate-900 font-mono font-bold text-lg flex-1">{resetPasswordData.password}</p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(resetPasswordData.password);
                        toast.success('Passwort kopiert!');
                      }}
                    >
                      📋 Kopieren
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-amber-800">
                  ⚠️ <strong>Wichtig:</strong> Das Passwort kann nach dem Schließen nicht mehr abgerufen werden!
                </p>
              </div>

              <Button onClick={() => setResetPasswordData(null)} className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                Verstanden, fortfahren
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
